import { TIME_ZONES } from '@js/constants/js-constants.es6.js';
import InvalidLuxonDateError from "@js/errors/invalid-luxon-date-error.es6.js";
import {DateTime} from "luxon";

export default class LuxonUtils {
  /**
   * Check if the provided date is an instance of a Date and throw an TypeError if not.
   * @param {Date} date the date to check
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {void}
   */
  static #validateDate(date) {
    if (!(date instanceof Date)) {
      throw new TypeError('Date must be an instance of a Date.');
    }
  }

  /**
   * Convert the provided JavaScript date to a Luxon date object, in local time.
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {object} the Luxon DateTime instance
   */
  static jsDateToLocalLuxonDate(date) {
    LuxonUtils.#validateDate(date);
    // eslint-disable-next-line new-cap
    const localTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const luxonDate = DateTime.fromJSDate(date, {zone: localTimeZoneName});

    if (!luxonDate.isValid) {
      throw new InvalidLuxonDateError(
        date,
        luxonDate.invalidReason,
        luxonDate.invalidExplanation,
      );
    }

    return luxonDate.setZone(TIME_ZONES.LOCAL, { keepLocalTime: true });
  }

  /**
   * Convert the provided JavaScript date to an ISO date representation.
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the ISO date
   */
  static jsDateToIsoDate(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.toISODate();
  }

  /**
   * Get the month number of the provided JavaScript date.
   * For example: Mon Oct 16 2023 21:16:27 GMT-0400 (Eastern Daylight Time) would return 10
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {number} the month number
   */
  static getMonthNumber(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.month;
  }

  /**
   * Get the day number of the provided JavaScript date.
   * For example: Mon Oct 16 2023 21:16:27 GMT-0400 (Eastern Daylight Time) would return 16
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {number} the day number
   */
  static getDayNumber(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.day;
  }

  /**
   * Get the long weekday name of the provided JavaScript date.
   * For example: Mon Oct 16 2023 21:16:27 GMT-0400 (Eastern Daylight Time) would return Monday
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the long weekday name
   */
  static getWeekdayLongName(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.weekdayLong;
  }

  /**
   * Get the short date string of the provided JavaScript date.
   * For example: Mon Oct 16 2023 21:16:27 GMT-0400 (Eastern Daylight Time) would return 10/16/23
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the simple time string
   */
  static getShortDateString(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.toLocaleString(DateTime.DATE_SHORT);
  }

  /**
   * Get the simple time string of the provided JavaScript date.
   * For example: Mon Oct 16 2023 21:16:27 GMT-0400 (Eastern Daylight Time) would return 9:16 AM
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the simple time string
   */
  static getSimpleTimeString(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.toLocaleString(DateTime.TIME_SIMPLE);
  }

  /**
   * Get the short offset name of the provided JavaScript date.
   * For example: Mon Oct 16 2023 21:16:27 GMT-0400 (Eastern Daylight Time) would return EDT
   *
   * @param {Date} date a JavaScript date
   * @throws {TypeError} if the provided date is not an instance of a Date
   * @return {string} the short offset name
   */
  static getOffsetShortName(date) {
    LuxonUtils.#validateDate(date);
    const luxonDate = LuxonUtils.jsDateToLocalLuxonDate(date);
    return luxonDate.offsetNameShort;
  }

  /**
   * Given a date string like 2024-03-06 return Wed Mar 06.
   * @param {String} dateString a string like 2024-03-06.
   * @return {*} a stringified date like Wed Mar 06.
   */
  static convertIsoDateToStringified(dateString) {
    const date = DateTime.fromISO(dateString);
    return date.setLocale('en-US').toFormat('ccc MMM d');
  }
}
